import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center rounded-2xl justify-center whitespace-nowrap text-sm font-medium transition-colors outline-0 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primaryFilled: "bg-primary-p text-white",
        secondaryFilled: "bg-secondary-s text-white",
        whiteFilled: "bg-neutral-0 text-black",
        primaryShaded: "bg-primary-shaded text-primary-p",
        secondaryShaded: "bg-secondary-shaded text-secondary-s",
        whiteShaded: "bg-neutral-shaded text-neutral-0",
        danger: "bg-[#BC0F0F14] text-[#BC0F0F]",
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        blue:"bg-[#3C3599] text-white hover:!opacity-70 transition-opacity",
        green:"bg-old-main text-white hover:!opacity-70 transition-opacity",
        blueShaded:"text-[#3C3599] bg-[#efeff7] hover:!opacity-70 transition-opacity",
        greenShaded:"text-old-main bg-[#efeff7] hover:!opacity-70 transition-opacity",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-7 px-1 md:px-8 md:h-9",
        lg: "h-11 rounded-md px-8",
        icon: "h-5 w-5",
        base: "h-5 px-1 md:px-3  md:h-7"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
